/**
 * 本文の改行コードと、contenteditable からの本文の読み取り。
 *
 * 本文の改行は LF に統一して保存する。
 * contenteditable="true" のエディタは、Enter で <div> や <br> を作るため、
 * innerText で読むと改行が二重になることがある（例: `a\n<div>b</div>` → `a\n\nb`）。
 * ここでは DOM をたどり、画面に見える行のとおりに本文へ戻す。
 */

// テスト用に、DOM の必要な部分だけを型にしている。
type EditableNode = {
  nodeType: number;
  nodeName: string;
  data?: string;
  childNodes: ArrayLike<EditableNode>;
};

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

const blockElements = new Set([
  'DIV',
  'P',
  'LI',
  'UL',
  'OL',
  'PRE',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TABLE',
  'TR',
]);

const skippedElements = new Set(['SCRIPT', 'STYLE', 'svg', 'SVG']);

/** CRLF・CR を LF にそろえる。 */
export function normalizeLineBreaks(text: string): string {
  return String(text ?? '').replace(/\r\n?/g, '\n');
}

function isBlock(node: EditableNode): boolean {
  return node.nodeType === ELEMENT_NODE && blockElements.has(node.nodeName.toUpperCase());
}

// 親と、その中での位置。<br> の後ろに本文が続くかを、祖先をさかのぼって調べるために使う。
type Position = { parent: EditableNode; index: number };

function hasFollowingContent({ parent, index }: Position): boolean {
  const children = parent.childNodes;
  for (let i = index + 1; i < children.length; i += 1) {
    const sibling = children[i];
    if (sibling.nodeType === TEXT_NODE && (sibling.data ?? '') === '') {
      continue;
    }
    return true;
  }
  return false;
}

// ブロックの末尾の <br> は、空行やカーソル位置を保つための置き物で、改行ではない。
// <b> などのインライン要素の末尾にあっても、その後ろに本文が続けば改行として数える。
// inlinePath は、いちばん近いブロック（またはエディタ本体）からの、インライン要素の位置の並び。
function isTrailingBr(position: Position, inlinePath: Position[]): boolean {
  return !hasFollowingContent(position) && !inlinePath.some(hasFollowingContent);
}

/** contenteditable の本文を、画面の行のとおりに LF 区切りの文字列へ戻す。 */
export function readEditableText(root: EditableNode): string {
  const lines: string[] = [];
  let current = '';
  // 改行の直後は、次の行が始まっている（空行でも 1 行として数える）。
  let lineStarted = false;
  // 今の行が、テキストの末尾の LF だけで始まった空の行かどうか。
  // ブロックの末尾の LF は画面に出ないため（Shift+Enter で b\n\n ができる）、その行は数えない。
  let startedByTrailingLf = false;

  const endLine = () => {
    lines.push(current);
    current = '';
  };

  const appendText = (text: string) => {
    const parts = normalizeLineBreaks(text)
      .replace(/\u00a0/g, ' ')
      .split('\n');
    parts.forEach((part, index) => {
      if (index > 0) {
        endLine();
        lineStarted = true;
        startedByTrailingLf = true;
      }
      if (part !== '') {
        current += part;
        lineStarted = true;
        startedByTrailingLf = false;
      }
    });
  };

  // 戻り値は、何か本文を書き出したかどうか。
  const walk = (node: EditableNode, inlinePath: Position[] = []): boolean => {
    let wrote = false;
    const children = node.childNodes;
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      if (child.nodeType === TEXT_NODE) {
        const data = child.data ?? '';
        appendText(data);
        wrote = wrote || data !== '';
      } else if (child.nodeType !== ELEMENT_NODE || skippedElements.has(child.nodeName)) {
        continue;
      } else if (child.nodeName.toUpperCase() === 'BR') {
        if (!isTrailingBr({ parent: node, index: i }, inlinePath)) {
          endLine();
          lineStarted = true;
          wrote = true;
        }
        // 末尾の <br> は置き物だが、直前の LF で始まった空の行を画面に見せる
        startedByTrailingLf = false;
      } else if (isBlock(child)) {
        // ブロックは新しい行から始まる。直前が改行で終わっていれば、その空の行をそのまま使う。
        if (current !== '') {
          endLine();
        }
        lineStarted = false;
        const blockWrote = walk(child);
        // ブロック内の改行のあとの空行（b<br><br> や b\n\n）も 1 行として残す。
        // 中身のないブロック（<div><br></div>）は空行 1 つ
        if (current !== '' || (lineStarted && !startedByTrailingLf) || !blockWrote) {
          endLine();
        }
        lineStarted = false;
        startedByTrailingLf = false;
        wrote = true;
      } else {
        wrote = walk(child, [{ parent: node, index: i }, ...inlinePath]) || wrote;
      }
    }
    return wrote;
  };

  walk(root);
  if (current !== '' || lineStarted) {
    endLine();
  }
  return lines.join('\n');
}
